import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Building2, Palette, Target, MessageCircle, CheckCircle2, ArrowRight, ArrowLeft, Loader2, PartyPopper,
} from "lucide-react";

const STEPS = [
  { n: 1, title: "Informazioni azienda", icon: Building2 },
  { n: 2, title: "Branding", icon: Palette },
  { n: 3, title: "Utilizzo", icon: Target },
  { n: 4, title: "Primo canale", icon: MessageCircle },
  { n: 5, title: "Completamento", icon: PartyPopper },
];

const GOALS = [
  { id: "support", label: "Assistenza clienti" },
  { id: "bookings", label: "Comunicazioni prenotazioni" },
  { id: "payments", label: "Promemoria pagamenti" },
  { id: "marketing", label: "Campagne marketing" },
  { id: "automations", label: "Automazioni" },
  { id: "notifications", label: "Notifiche" },
  { id: "external", label: "Integrazione con app esterne" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { loadMe } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState({ name: "", legal_name: "", industry: "", country: "Italia", language: "it", timezone: "Europe/Rome", currency: "EUR", phone: "", email: "", website: "" });
  const [branding, setBranding] = useState({ logo_url: "", display_name: "", primary_color: "#10b981", secondary_color: "#0f172a", theme_mode: "light" });
  const [goals, setGoals] = useState([]);

  useEffect(() => {
    api.get("/onboarding").then(({ data }) => {
      if (data.data?.completed) navigate("/dashboard");
      if (data.data?.current_step) setStep(data.data.current_step);
    });
    api.get("/tenant").then(({ data }) => {
      setCompany((c) => ({ ...c, name: data.data.name || "", industry: data.data.industry || "" }));
      setBranding((b) => ({ ...b, display_name: data.data.display_name || data.data.name || "" }));
    }).catch(() => {});
  }, []);

  const progress = Math.round(((step - 1) / STEPS.length) * 100);

  const saveStep = async (data, completed = false) => {
    setLoading(true);
    try {
      await api.post("/onboarding/step", { step, data, completed });
      if (step < 5) setStep(step + 1);
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const toggleGoal = (id) => setGoals((g) => g.includes(id) ? g.filter((x) => x !== id) : [...g, id]);

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/step", { step: 5, data: {}, completed: true });
      await api.post("/onboarding/complete");
      await loadMe();
      toast.success("Configurazione completata!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="border-b border-border bg-background">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-6">
          <Logo />
          <button onClick={() => navigate("/dashboard")} className="text-sm text-muted-foreground hover:text-foreground" data-testid="skip-onboarding">
            Salta e vai alla dashboard
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-10">
        {/* Stepper */}
        <div className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            {STEPS.map((s) => {
              const Icon = s.icon;
              const active = s.n === step;
              const done = s.n < step;
              return (
                <div key={s.n} className="flex flex-1 flex-col items-center gap-1.5">
                  <div className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${done ? "border-primary bg-primary text-primary-foreground" : active ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
                    {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span className={`hidden text-xs sm:block ${active ? "font-medium text-foreground" : "text-muted-foreground"}`}>{s.title}</span>
                </div>
              );
            })}
          </div>
          <Progress value={progress} className="h-1.5" />
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="onboarding-card">
          {step === 1 && (
            <div className="space-y-4">
              <h2 className="font-heading text-2xl font-semibold">Informazioni azienda</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Nome azienda"><Input value={company.name} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="onb-company-name" /></Field>
                <Field label="Ragione sociale"><Input value={company.legal_name} onChange={(e) => setCompany({ ...company, legal_name: e.target.value })} /></Field>
                <Field label="Settore"><Input value={company.industry} onChange={(e) => setCompany({ ...company, industry: e.target.value })} /></Field>
                <Field label="Nazione"><Input value={company.country} onChange={(e) => setCompany({ ...company, country: e.target.value })} /></Field>
                <Field label="Lingua">
                  <Select value={company.language} onValueChange={(v) => setCompany({ ...company, language: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="it">Italiano</SelectItem><SelectItem value="en">English</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Fuso orario"><Input value={company.timezone} onChange={(e) => setCompany({ ...company, timezone: e.target.value })} /></Field>
                <Field label="Valuta">
                  <Select value={company.currency} onValueChange={(v) => setCompany({ ...company, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="EUR">EUR €</SelectItem><SelectItem value="USD">USD $</SelectItem><SelectItem value="GBP">GBP £</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Telefono"><Input value={company.phone} onChange={(e) => setCompany({ ...company, phone: e.target.value })} /></Field>
                <Field label="Email aziendale"><Input type="email" value={company.email} onChange={(e) => setCompany({ ...company, email: e.target.value })} /></Field>
                <Field label="Sito web"><Input value={company.website} onChange={(e) => setCompany({ ...company, website: e.target.value })} /></Field>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <h2 className="font-heading text-2xl font-semibold">Branding</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="URL Logo"><Input value={branding.logo_url} onChange={(e) => setBranding({ ...branding, logo_url: e.target.value })} placeholder="https://..." data-testid="onb-logo" /></Field>
                <Field label="Nome visualizzato"><Input value={branding.display_name} onChange={(e) => setBranding({ ...branding, display_name: e.target.value })} /></Field>
                <Field label="Colore principale">
                  <div className="flex gap-2"><Input type="color" value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} className="h-10 w-14 p-1" /><Input value={branding.primary_color} onChange={(e) => setBranding({ ...branding, primary_color: e.target.value })} /></div>
                </Field>
                <Field label="Colore secondario">
                  <div className="flex gap-2"><Input type="color" value={branding.secondary_color} onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })} className="h-10 w-14 p-1" /><Input value={branding.secondary_color} onChange={(e) => setBranding({ ...branding, secondary_color: e.target.value })} /></div>
                </Field>
              </div>
              <Field label="Modalità tema">
                <div className="flex gap-2">
                  {["light", "dark"].map((m) => (
                    <button key={m} onClick={() => setBranding({ ...branding, theme_mode: m })} className={`flex-1 rounded-lg border px-4 py-2.5 text-sm capitalize transition-colors ${branding.theme_mode === m ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>{m === "light" ? "Chiara" : "Scura"}</button>
                  ))}
                </div>
              </Field>
              <div className="rounded-xl border border-border p-4">
                <p className="mb-2 text-xs font-medium text-muted-foreground">Anteprima</p>
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl text-white" style={{ background: branding.primary_color }}>
                    {(branding.display_name || "C")[0]}
                  </div>
                  <span className="font-heading font-semibold" style={{ color: branding.secondary_color }}>{branding.display_name || "La tua azienda"}</span>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="font-heading text-2xl font-semibold">Come userai la piattaforma?</h2>
              <p className="text-sm text-muted-foreground">Seleziona una o più opzioni</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {GOALS.map((g) => (
                  <label key={g.id} className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${goals.includes(g.id) ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`} data-testid={`goal-${g.id}`}>
                    <Checkbox checked={goals.includes(g.id)} onCheckedChange={() => toggleGoal(g.id)} />
                    <span className="text-sm font-medium">{g.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <h2 className="font-heading text-2xl font-semibold">Primo canale</h2>
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
                <MessageCircle className="mb-3 h-8 w-8 text-primary" />
                <p className="font-medium">Il collegamento a WhatsApp Business sarà configurato nella fase successiva.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  L'architettura è già pronta per l'integrazione con Meta WhatsApp Cloud API.
                  Per ora puoi creare un canale demo per esplorare la piattaforma.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" className="rounded-lg" onClick={() => { toast.success("Canale demo creato"); saveStep({ channel: "demo" }); }} data-testid="create-demo-channel">Crea canale demo</Button>
                <Button variant="ghost" className="rounded-lg" onClick={() => saveStep({ channel: "skipped" })}>Salta questo passaggio</Button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5 text-center">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <PartyPopper className="h-8 w-8" />
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold">Tutto pronto!</h2>
                <p className="mt-2 text-sm text-muted-foreground">La configurazione iniziale è completa.</p>
              </div>
              <div className="rounded-xl border border-border p-5 text-left">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium">Completamento configurazione</span>
                  <span className="text-sm font-semibold text-primary">80%</span>
                </div>
                <Progress value={80} className="h-2" />
                <ul className="mt-4 space-y-2 text-sm">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Profilo azienda configurato</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Branding impostato</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-primary" /> Obiettivi selezionati</li>
                  <li className="flex items-center gap-2 text-muted-foreground"><MessageCircle className="h-4 w-4" /> WhatsApp da collegare (fase 2)</li>
                </ul>
              </div>
              <Button onClick={finish} className="w-full rounded-lg" size="lg" disabled={loading} data-testid="finish-onboarding">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accedi a Connectivity
              </Button>
            </div>
          )}

          {step < 5 && (
            <div className="mt-8 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="onb-back">
                <ArrowLeft className="mr-1 h-4 w-4" /> Indietro
              </Button>
              {step !== 4 && (
                <Button onClick={() => saveStep(step === 1 ? company : step === 2 ? branding : { goals })} disabled={loading} className="rounded-lg" data-testid="onb-next">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Continua <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
